import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CCol, CRow, CForm,
  CFormInput, CFormLabel, CFormSelect, CFormCheck, CFormSwitch, CSpinner, CBadge,
  CTabs, CTabList, CTab, CTabContent, CTabPanel,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { normalizarNumeroTexto } from '../utils/numeros'
import { fmtMoneda } from '../utils/moneda'
import { useLocation, useNavigate } from 'react-router-dom'
import { abrirCheckoutEpayco } from '../utils/epayco'
import { estadoEmpresa } from '../utils/estadoEmpresa'
import { useTroniaEstado } from '../utils/troniaAcceso'
import { ENTIDAD_PAGO, TIPO_PAGO } from '../utils/pagoLabels'
import { useDispatch } from 'react-redux'
import TroniaPanel from '../components/TroniaPanel'
import { NotificacionesLista, useNotificaciones } from '../components/NotificacionesPanel'
import SubirPagoModal from '../components/SubirPagoModal'
import ModalPagoModulo from '../components/ModalPagoModulo'
import DatosDePago from '../components/DatosDePago'

// Paises del combo GX PaisCodigo (empresaloaddvcombo.cs:137-173 — lee
// dbo.Pais; el seed local deploy/dev/postgres/schema/seed_data.sql:5-20
// replica los 18 valores reales). PaisCodigo es FK a dbo.Pais: la lista
// debe usar estos codigos exactos.
// PaisNombre en BD tiene el formato "PAIS - Zona" (18 zonas IANA): la zona
// horaria del pais se deriva de la parte despues del " - ".
const PAISES: Array<[number, string]> = [
  [51, 'PERU - America/Lima'],
  [52, 'MEXICO - America/Mexico_City'],
  [54, 'ARGENTINA - America/Argentina/Buenos_Aires'],
  [55, 'BRASIL - America/Sao_Paulo'],
  [56, 'CHILE - America/Santiago'],
  [57, 'COLOMBIA - America/Bogota'],
  [502, 'GUATEMALA - America/Guatemala'],
  [503, 'EL SALVADOR - America/El_Salvador'],
  [504, 'HONDURAS - America/Tegucigalpa'],
  [505, 'NICARAGUA - America/Managua'],
  [506, 'COSTA RICA - America/Costa_Rica'],
  [507, 'PANAMA - America/Panama'],
  [591, 'BOLIVIA - America/La_Paz'],
  [593, 'ECUADOR - America/Guayaquil'],
  [595, 'PARAGUAY - America/Asuncion'],
  [598, 'URUGUAY - America/Montevideo'],
  [1787, 'PUERTO RICO - America/Puerto_Rico'],
  [1809, 'REPUBLICA DOMINICANA - America/Santo_Domingo'],
]

// Plan contratado (EmpresaLibreNumerico2 — PlanSiprinia GX: 1/2/3).
const PLAN_NOMBRE: Record<number, string> = { 1: 'Unipersonal', 2: 'Duo', 3: 'Empresa' }
// Moneda de la UI: fuente única utils/moneda.ts (fmtMoneda — símbolo
// configurado + máscara GX Z,ZZZ,ZZZ,ZZ9.99). [RGM] 2026-10-02 (F)
// Keys de los módulos del catálogo del API (Constantes.cs ModuloSiprinia):
// los precios salen del API, nunca hardcodeados. [RGM]
const KEY_FACTURACION = 'FACTURACION'
const KEY_FOTOS = 'CUSTOMER_PHOTOS'
const KEY_IMPRESION = 'MOBILE_PRINTING'
const KEY_WHATSAPP = 'WHATSAPP_CONFIRMATION'
// Tipo de pago GX del módulo (PagoTipo.ModuloFacturacion = 5): preselecciona
// el select en el modal compartido "Subir pago".
const TIPO_PAGO_FACTURACION = 5
// [RGM] 2026-09-29 (B4): tipo del pago de ACTUALIZACIÓN de plan (diferencia).
const TIPO_PAGO_ACTUALIZACION = 7

interface ModuloCatalogo {
  key: string; nombre: string; precio: number
  precioConImpresora?: number
}
interface CatalogoModulos { modulos: ModuloCatalogo[] }
// Ruta con el estado de sus módulos (GET /rutas — misma fuente que
// RutasPage): zonaImpresora/zonaSMS (0/1) + pagos SUBIDOS pendientes de
// confirmación (pendienteImpresora/pendienteWhatsapp). Solo lectura. [RGM]
interface RutaModuloRow {
  zonaCodigo?: number; zonaNombre?: string
  zonaImpresora?: number; zonaSMS?: number
  pendienteImpresora?: boolean; pendienteWhatsapp?: boolean
}
// Tipo de pago (combo GX subirpagosistema.cs:1022-1025).
// [RGM] 2026-09-17: etiquetas de los combos GX de pago centralizadas en
// utils/pagoLabels.ts (mismo texto en todas las grillas).
// Entidad del pago (combo GX subirpagosistema.cs:1033-1046; 11=Paypal).
// [RGM] 2026-09-17: ENTIDAD_PAGO ahora vive en utils/pagoLabels.ts.

interface CuentaData {
  codigo: string
  nombre: string
  email: string
  whatsapp: string
  repreLegal: string
  fechaCreacion: string
  estado: number
  plan: number
  // Atributos reales de la tabla Cuenta (CuentaController GET — cuenta_bc.cs:1968)
  direccion: string
  telefono: string
  paisCodigo: number
  ciudad: string
  trabajaSabado: boolean
  trabajaDomingo: boolean
  trabajaFestivo: boolean
  moduloFacturacion: number
  // %IVA de la empresa [RGM 2026-10-05]: EmpresaLibreNumerico1 — lo setea la
  // empresa (Mi Cuenta) si usa IVA; default 0.
  iva: number
  // [RGM] 2026-10-07 (feature F2): MODO DEL SISTEMA (EmpresaLibreCaracter3 —
  // PRESTAMOS | FACTURACION | HIBRIDO). Selector en Módulos y servicios.
  modo: string
  nombreMoneda: string
  tipoMoneda: string
  tipoServicio: string
  descripcion: string
  metaMaxima: number
  metaMinima: number
  rutas: number
  tarifaMes: number
  ultimoPago: string
  proximoCobro: string
  saldoActual: number
  motivoSaldo: string
  fotosAdicionalMes: boolean
  enrutarManual: number
  logoUrl: string
}

// Fila del historial de pagos (GET /pagos — replica pagoww GX + pasarelas).
interface PagoRow {
  pagoNro: number
  pagoFecha: string
  pagoValor: number
  pagoTipo: number
  pagoEntidad: number
  pagoRefrencia: string
  pagoConfirmado: number
  pagoObservacion: string
  // [RGM] 2026-10-09 (anti-duplicado): fecha del comprobante — la usa la
  // comparación tentativa cuando la referencia viene vacía.
  pagoFechaPago?: string | null
  // URL del comprobante (armada por el API desde la clave corta en R2). [RGM] 2026-09-16.
  comprobanteUrl?: string | null
}

// [RGM] 2026-10-09 (pedido Rafael — caso MAIN #19/#20: la misma referencia
// bancaria subida 2 veces): clave de agrupación anti-duplicado de un pago
// PENDIENTE. Con referencia: la referencia (trim). Sin referencia: valor +
// fecha de pago como tentativo (mismo criterio del aviso del SubirPagoModal).
const claveDuplicadoPago = (p: PagoRow): string => {
  const referencia = String(p.pagoRefrencia ?? '').trim()
  return referencia !== ''
    ? `REF:${referencia}`
    : `VF:${Number(p.pagoValor ?? 0)}:${String(p.pagoFechaPago ?? '').slice(0, 10)}`
}

// Cuenta de cobro mensual (GET /pagos/cuentas): fila Pago generada por el job
// con la marca "Cuenta de cobro". Estados: 0 pendiente · 2 comprobante subido
// · 1 confirmada · 3 anulada / no pagada. [RGM]
interface CuentaCobro {
  pagoNro: number
  fecha: string
  valor: number
  estado: number
  observacion: string
  periodo: string
  comprobanteUrl: string | null
}
interface CuentasCobro {
  cuentas: CuentaCobro[]
  totalPendiente: number
  saldoVencido: number
}

/**
 * Respuesta de GET /pagos/total-a-pagar (PagosController.TotalAPagar):
 * desglose de la mensualidad de la empresa.
 * [RGM] 2026-10-23 (pedido Rafael): ePayco traslada al cliente el 5% de
 * comisión de la pasarela — el API devuelve EpaycoTotal (total con recargo,
 * ReglasPago.MontoEpaycoConRecargo) y EpaycoRecargo (total − base).
 */
interface ResumenPago {
  totalAPagar?: number
  tarifaMensual?: number
  mesesVencidos?: number
  otrosSaldosPendientes?: number
  motivoSaldo?: string
  tarifaBase?: number
  tarifaFotos?: number
  tarifaRutas?: number
  tarifaTronia?: number
  tarifaAjuste?: number
  epaycoTotal?: number
  epaycoRecargo?: number
}

/**
 * Mi Cuenta — replica de cuenta.cs:173 (SiprinMax GX, Mode=UPD).
 * Toda la UI usa SOLO atributos reales de la tabla Cuenta (cuenta_bc.cs:1968).
 * Logo R2 → EmpresaLogo_GXI (empresa.cs:616-619) [RGM]: el logo se sube a
 * Cloudflare R2 y la URL se persiste en EmpresaLogo_GXI, nunca en binario.
 * Tabs sin datos reales (Seguridad, Notificaciones, Miembros, Permisos,
 * TronIA) quedan como estructura vacia: "Sin informacion disponible".
 */
const CuentaPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? ''
  const navigate = useNavigate()
  // [RGM] 2026-10-01 (pedido Rafael): notificaciones del tab 5 — los avisos
  // vivos + la nueva versión del APK; ✕ descarta local.
  const { avisos: notifAvisos, descartar: descartarNotif } = useNotificaciones()
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')
  const [activeKey, setActiveKey] = useState(1)
  // [RGM] 2026-09-29 (reporte Rafael: "¿por qué se pone TronIA activo si está
  // en prueba?"): la tarjeta usa el ESTADO real de TronIA (PRUEBA ≠ plan
  // activo); antes el booleano de acceso pintaba "Activo / Plan activo." en
  // plena prueba gratuita.
  const troniaEstado = useTroniaEstado()

  // Pago de la MENSUALIDAD por pasarela (Configuracion/Empresa/Pagos): ahora
  // son las CUENTAS DE COBRO del período las que se pagan — el backend, con el
  // tipo 'mensualidad', cobra la SUMA de las pendientes y las confirma. [RGM]
  const [pagando, setPagando] = useState(false)
  const [cuentasCobro, setCuentasCobro] = useState<CuentasCobro | null>(null)
  // Cuenta cuyo comprobante se está subiendo (pagoNro) — spinner por fila.
  const [subiendoComprobanteCuenta, setSubiendoComprobanteCuenta] = useState<number | null>(null)
  const cargarCuentasCobro = () => {
    api.get<CuentasCobro>('/pagos/cuentas')
      .then(setCuentasCobro)
      .catch(() => {})
  }
  useEffect(cargarCuentasCobro, [])

  // [RGM] Modal COMPARTIDO "Subir pago de Suscripción" (transferencia/giro):
  // el formulario vive en components/SubirPagoModal (también lo usa PagoPage).
  const [subirPagoAbierto, setSubirPagoAbierto] = useState(false)
  // Tipo preseleccionado del modal compartido (5 = Módulo Facturación desde su
  // tarjeta; '' = formulario genérico de suscripción).
  const [tipoInicialSubirPago, setTipoInicialSubirPago] = useState<number | ''>('')

  // Catálogo de módulos (/pago/planes-siprinia — misma fuente que PagoPage y
  // RutasPage): los precios SIEMPRE vienen del API (override de entorno
  // incluido), nunca hardcodeados. [RGM]
  const [modulosCatalogo, setModulosCatalogo] = useState<ModuloCatalogo[]>([])
  useEffect(() => {
    api.get<CatalogoModulos>('/pago/planes-siprinia')
      .then((c) => setModulosCatalogo(c.modulos ?? []))
      .catch(() => undefined)
  }, [])

  // Estado de los módulos POR RUTA (impresión/WhatsApp) del tab "Módulos y
  // servicios": misma fuente que RutasPage (GET /rutas → zonaImpresora/
  // zonaSMS por ruta). Solo lectura; si falla, las tarjetas quedan
  // informativas. Se recarga al cerrar el modal de pago. [RGM]
  const [rutasModulos, setRutasModulos] = useState<RutaModuloRow[]>([])
  const cargarRutasModulos = useCallback(() => {
    if (!empresa) return
    api.get<RutaModuloRow[]>(`/rutas?empresa=${encodeURIComponent(empresa)}`)
      .then((r) => setRutasModulos(r ?? []))
      .catch(() => undefined)
  }, [empresa])
  useEffect(cargarRutasModulos, [cargarRutasModulos])

  // [RGM] 2026-09-28 (pedido Rafael): al encender el switch de una ruta OFF se
  // abre el modal de pago del módulo (PayPal / ePayco / comprobante) para ESA
  // ruta; si ya está ON, el switch queda bloqueado (solo el admin desactiva).
  const [moduloPago, setModuloPago] = useState<{ tipo: string; zonaCodigo: number; etiqueta: string } | null>(null)
  const abrirPagoModulo = (tipo: string, r: RutaModuloRow, nombreModulo: string) => {
    const nombreRuta = r.zonaNombre?.trim() || `Ruta ${r.zonaCodigo ?? ''}`
    setModuloPago({
      tipo,
      zonaCodigo: Number(r.zonaCodigo ?? 0),
      etiqueta: `${nombreModulo} — ${nombreRuta}`,
    })
  }

  // [RGM] Al volver del checkout con el botón ATRÁS (bfcache), `pagando` podía
  // quedar en true y los botones de pago deshabilitados hasta refrescar
  // (reporte Rafael) — se limpia al restaurarse la página.
  useEffect(() => {
    const alRestaurar = (e: PageTransitionEvent) => {
      if (e.persisted) setPagando(false)
    }
    window.addEventListener('pageshow', alRestaurar)
    return () => window.removeEventListener('pageshow', alRestaurar)
  }, [])

  // Historial de pagos de la empresa (aceptados por las pasarelas + subidos a
  // mano): se recarga al entrar y tras cada pago. [RGM] 2026-09-16 (pedido Rafael).
  const [pagos, setPagos] = useState<PagoRow[]>([])
  const cargarPagos = () => {
    api.get<PagoRow[]>(`/pagos?empresa=${encodeURIComponent(empresa)}`)
      .then(setPagos)
      .catch(() => {})
  }
  useEffect(cargarPagos, [])

  // [RGM] 2026-10-09 (pedido Rafael — anti-duplicado de pagos): claves de las
  // filas PENDIENTES (PagoConfirmado = 2) que comparten referencia con OTRA
  // pendiente de la empresa → "Posible duplicado" en el historial, ANTES de
  // que el administrador confirme (caso real MAIN #19/#20).
  const duplicadosPendientes = useMemo(() => {
    const porClave = new Map<string, number>()
    for (const p of pagos) {
      if (Number(p.pagoConfirmado) !== 2) continue
      const clave = claveDuplicadoPago(p)
      porClave.set(clave, (porClave.get(clave) ?? 0) + 1)
    }
    return new Set(
      [...porClave.entries()].filter(([, veces]) => veces > 1).map(([clave]) => clave),
    )
  }, [pagos])

  // [RGM] 2026-09-29 (pedido Rafael): las cuentas de cobro SIN pagar no se
  // muestran en el Historial — viven solo como cuenta de cobro hasta que se
  // confirmen (pagadas). Los demás pendientes (subidos) sí se muestran.
  const pagosVisibles = pagos.filter((p) =>
    !(p.pagoObservacion ?? '').trim().startsWith('Cuenta de cobro') || p.pagoConfirmado === 1)

  // Desglose del Total a pagar (meses vencidos × tarifa + otros saldos).
  const [resumenPago, setResumenPago] = useState<ResumenPago | null>(null)

  const pagarMensualidad = async (pasarela: 'paypal' | 'epayco', tipo: string = 'mensualidad') => {
    setPagando(true)
    try {
      if (pasarela === 'paypal') {
        // PayPal: approvalUrl → redirect. El contexto queda en sessionStorage
        // para capturar la orden al volver (?paypal=ok). ePayco NO lo usa: la
        // activación la hace el webhook de confirmación.
        sessionStorage.setItem('pagoCtx', JSON.stringify({ tipo }))
        const r = await api.post<{ approvalUrl?: string }>('/pago/paypal/orden', { tipo })
        if (r.approvalUrl) window.location.href = r.approvalUrl
        return
      }
      // ePayco checkout v2 [RGM] 2026-09-15: el backend crea la sesión y aquí
      // se abre el widget con checkout-v2.js.
      const r = await api.post<{ sessionId?: string; test?: boolean }>('/pago/epayco/orden', { tipo })
      if (!r.sessionId) {
        alert('No se pudo iniciar el pago con ePayco.')
        return
      }
      await abrirCheckoutEpayco(r.sessionId, !!r.test)
    } catch (err: any) {
      alert(err?.message ?? 'No se pudo iniciar el pago.')
      sessionStorage.removeItem('pagoCtx')
    } finally {
      setPagando(false)
    }
  }
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [cuenta, setCuenta] = useState<CuentaData | null>(null)
  // [RGM] 2026-09-28 (reporte Rafael): el tab TronIA sirve para ELEGIR/CAMBIAR
  // el plan del servicio → visible con la empresa sin mora (estado != 3),
  // tenga o no plan (antes exigía plan activo y no había forma de activarlo).
  const puedeGestionarTronia = Number(cuenta?.estado ?? 0) !== 3
  const dispatch = useDispatch()

  const cargar = () => {
    setCargando(true)
    setError('')
    api.get<CuentaData>(`/cuenta?empresa=${encodeURIComponent(empresa)}`)
      .then(res => {
        setCuenta(res)
        if (res.logoUrl) setLogoPreview(res.logoUrl)
        // Sincroniza logo/nombre de la empresa al store global: el avatar del
        // Navbar lo muestra sin recargar la pagina.
        dispatch({ type: 'set', empresaLogo: res.logoUrl ?? null, empresaNombre: res.nombre })
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
    // [RGM] 2026-09-29: desglose del Total a pagar para la tarjeta "Pagos".
    api.get<ResumenPago>(`/pagos/total-a-pagar?empresa=${encodeURIComponent(empresa)}`)
      .then(setResumenPago)
      .catch(() => undefined)
  }
  useEffect(cargar, [empresa])

  // [RGM] El FAB TronIA navega a /mi-cuenta#tronia → enfocar el tab TronIA (11).
  // [RGM] 2026-09-28 (reporte Rafael): el tab de gestión existe con la empresa
  // sin mora (para elegir/cambiar plan), tenga o no plan activo.
  // [RGM] Fix (reporte Rafael: el botón necesitaba refrescar): el efecto debe
  // reaccionar a los cambios de hash, no solo al montar (location.hash).
  const location = useLocation()
  useEffect(() => {
    if (location.hash === '#tronia' && puedeGestionarTronia) setActiveKey(11)
  }, [location.hash, puedeGestionarTronia])

  const set = (campo: keyof CuentaData, valor: any) =>
    setCuenta((c) => (c ? { ...c, [campo]: valor } : c))

  // [RGM] 2026-09-28 (diseño Rafael — switch estilo panel superadmin): activa
  // o desactiva Fotos del cliente de inmediato (POST /cuenta/fotos; su valor
  // se cobra en la mensualidad del 5), sin esperar "Guardar Cambios".
  const [guardandoFotos, setGuardandoFotos] = useState(false)
  const activarFotos = async (valor: boolean) => {
    setGuardandoFotos(true)
    setError('')
    setExito('')
    try {
      await api.post('/cuenta/fotos', { activo: valor })
      set('fotosAdicionalMes', valor)
      setExito(valor
        ? 'Fotos del cliente ACTIVADO — se cobra en tu mensualidad.'
        : 'Fotos del cliente desactivado.')
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo actualizar Fotos del cliente.')
    } finally {
      setGuardandoFotos(false)
    }
  }

  // [RGM] 2026-10-05 (pedido Rafael): el %IVA se actualiza INLINE — la tarjeta
  // de Módulos no tiene "Guardar Cambios" (ese botón es solo del Perfil); mismo
  // patrón del switch Fotos (POST /cuenta/iva al salir del campo).
  const [guardandoIva, setGuardandoIva] = useState(false)
  const actualizarIvaInline = async () => {
    if (!cuenta) return
    setGuardandoIva(true)
    setError('')
    setExito('')
    try {
      // Entero 0..100 (el backend valida igual) — evita 400 por decimales
      // del input libre (review GGA 2026-10-05). [RGM]
      const iva = Math.min(100, Math.max(0, Math.trunc(Number(cuenta.iva) || 0)))
      await api.post('/cuenta/iva', { iva })
      set('iva', iva)
      setExito('IVA actualizado.')
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo actualizar el IVA.')
    } finally {
      setGuardandoIva(false)
    }
  }

  // [RGM] 2026-10-07 (feature F2 — diseño Rafael: "el motor de sistema...
  // los tres switch en las tres tarjetas"): MOTOR DEL SISTEMA — radio de TRES
  // estados: Solo Préstamos (default) · Híbrido · Solo Facturación. Aplica
  // INLINE (sin recargar). Tarifas y datos no cambian.
  const [guardandoModo, setGuardandoModo] = useState(false)
  const motor = cuenta?.modo === 'FACTURACION'
    ? 'FACTURACION'
    : Number(cuenta?.moduloFacturacion ?? 0) === 1 ? 'HIBRIDO' : 'PRESTAMOS'
  const cambiarMotor = async (tipo: 'PRESTAMOS' | 'HIBRIDO' | 'FACTURACION') => {
    if (!cuenta || motor === tipo) return // el radio no se apaga tocándose a sí mismo
    const etiqueta = tipo === 'PRESTAMOS' ? 'Solo Préstamos' : tipo === 'HIBRIDO' ? 'Híbrido' : 'Solo Facturación'
    const ok = window.confirm(
      `¿Cambiar tu sistema a "${etiqueta}"?\n\n` +
      (tipo === 'FACTURACION'
        ? 'Dejarás de ver el módulo de Préstamos y el Módulo Facturación queda fijo. '
        : tipo === 'HIBRIDO'
          ? 'Verás Préstamos + Facturación con el módulo activo. '
          : 'Volverás al sistema de préstamos (sin Facturación). ') +
      'Tus tarifas y tus datos no cambian.')
    if (!ok) return
    setGuardandoModo(true)
    setError('')
    setExito('')
    try {
      // [RGM] 2026-10-07 (regla Rafael: "solo facturación SÍ cobra el plan
      // más los 500, porque internamente usa el motor de préstamos"): el
      // módulo NUNCA se regala — si no está pagado, queda pendiente y se
      // activa con el pago (tarjeta del Módulo Facturación, abajo).
      const debeCobrarModulo = tipo !== 'PRESTAMOS'
        && Number(cuenta.moduloFacturacion ?? 0) === 0
      const r = await api.post<any>('/cuenta/modo', {
        soloFacturacion: tipo === 'FACTURACION',
        moduloFacturacion: tipo !== 'PRESTAMOS'
          && Number(cuenta.moduloFacturacion ?? 0) === 1,
      })
      set('modo', String(r?.modo ?? (tipo === 'FACTURACION' ? 'FACTURACION' : 'PRESTAMOS')))
      set('moduloFacturacion', Number(r?.moduloFacturacion ?? 0))
      setExito(debeCobrarModulo
        ? 'Motor actualizado. El Módulo Facturación se activa con el pago (tarjeta de abajo, pago único).'
        : 'Motor del sistema actualizado.')
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo cambiar el motor del sistema.')
    } finally {
      setGuardandoModo(false)
    }
  }

  // para persistir la URL recién subida sin depender del estado asíncrono de
  // React (el upload se guarda solo, sin pulsar "Guardar Cambios"). [RGM] 2026-09-15.
  const guardarCuenta = async (logoUrlOverride?: string) => {
    if (!cuenta) return
    const logoUrl = logoUrlOverride ?? cuenta.logoUrl
    setError('')
    setExito('')
    try {
      const res = await api.post<{ message: string }>('/cuenta', {
        empresaCodigo: cuenta.codigo,
        nombre: cuenta.nombre,
        email: cuenta.email,
        whatsapp: cuenta.whatsapp,
        trabajaSabado: cuenta.trabajaSabado,
        // fotosAdicionalMes se conserva en el body aunque no tenga checkbox:
        // preserva EmpresaFactura (valor real) al guardar.
        fotosAdicionalMes: cuenta.fotosAdicionalMes,
        enrutarManual: Number(cuenta.enrutarManual),
        metaMaxima: Number(cuenta.metaMaxima),
        metaMinima: Number(cuenta.metaMinima),
        logoUrl,
        direccion: cuenta.direccion,
        telefono: cuenta.telefono,
        paisCodigo: Number(cuenta.paisCodigo),
        ciudad: cuenta.ciudad,
        trabajaDomingo: cuenta.trabajaDomingo,
        trabajaFestivo: cuenta.trabajaFestivo,
        // moduloFacturacion viaja SIEMPRE (su check es solo estado): el POST de
        // /cuenta pisa EmpresaModuloFacturacion con lo que llegue.
        moduloFacturacion: Number(cuenta.moduloFacturacion),
        // %IVA (EmpresaLibreNumerico1) — [RGM] 2026-10-05: la empresa lo setea
        // si usa IVA (default 0). Viaja siempre en el POST de Mi Cuenta.
        iva: Number(cuenta.iva) || 0,
        tipoMoneda: cuenta.tipoMoneda,
      })
      setExito(res.message)
      // Re-sincroniza el store tras guardar (nombre editable + logoUrl persistido)
      dispatch({ type: 'set', empresaLogo: logoUrl, empresaNombre: cuenta.nombre })
    } catch (err: any) {
      setError(err.message)
    }
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    await guardarCuenta()
  }

  // Sube el comprobante de UNA cuenta de cobro: queda en estado 2 (esperando la
  // confirmación del admin). [RGM] decisión Rafael: el pago por otro medio
  // exige el comprobante EN CADA cuenta (aunque sea por el valor total).
  const subirComprobanteCuenta = async (pagoNro: number, file: File) => {
    setError('')
    setExito('')
    setSubiendoComprobanteCuenta(pagoNro)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.post(`/pagos/cuentas/${pagoNro}/comprobante`, fd)
      setExito('Comprobante subido — queda pendiente de confirmación.')
      cargarCuentasCobro()
      cargarPagos()
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo subir el comprobante.')
    } finally {
      setSubiendoComprobanteCuenta(null)
    }
  }

  // Chip del estado de una cuenta de cobro (0 pendiente · 2 comprobante subido
  // · 1 confirmada · 3 anulada / no pagada). [RGM]
  const estadoCuenta = (estado: number) => {
    switch (estado) {
      case 0: return <CBadge color="warning" className="text-dark">Pendiente de pago</CBadge>
      case 2: return <CBadge color="info">Comprobante subido — esperando confirmación</CBadge>
      case 1: return <CBadge color="success">Confirmada</CBadge>
      case 3: return <CBadge color="secondary">Anulada / no pagada</CBadge>
      default: return <CBadge color="secondary">{estado}</CBadge>
    }
  }

  // [RGM] 2026-10-02 (cuentas-cobro-dia1): vencimiento de una cuenta PENDIENTE
  // (0/2). Se compara SOLO la fecha (sin hora) contra el día 5 del mes de su
  // PagoFecha: POR VENCER los días 1-5, VENCIDA desde el 6 (el job genera la
  // cuenta el día 1 y el período cierra el 5). El flujo de pago/comprobante
  // existente no cambia.
  const estadoVencimiento = (fechaPago: string): 'VENCIDA' | 'POR VENCER' => {
    const partes = String(fechaPago ?? '').slice(0, 10).split('-').map(Number)
    if (partes.length !== 3 || partes.some((n) => Number.isNaN(n))) return 'POR VENCER'
    const vence = new Date(partes[0], partes[1] - 1, 5)
    const hoy = new Date()
    hoy.setHours(0, 0, 0, 0)
    return hoy > vence ? 'VENCIDA' : 'POR VENCER'
  }

  const onLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Preview local INMEDIATO (objeto local) antes de subir a R2
    setLogoPreview(URL.createObjectURL(file))

    // Sube el archivo a R2. Intenta primero con resize a webp (150px); si el
    // canvas falla (formato no soportado, webp no disponible), sube el archivo
    // ORIGINAL tal cual — el logo siempre debe poder cargarse.
    const subir = async (blob: Blob, nombre: string) => {
      const formData = new FormData()
      formData.append('file', blob, nombre)
      const res = await api.post<{ url: string }>('/cuenta/upload-logo', formData)
      set('logoUrl', res.url)
      setLogoPreview(res.url) // la URL persistida es la definitiva
      // Reflejo inmediato del nuevo logo en el Navbar (solo el logo: el nombre
      // de la empresa no cambia al subir una imagen).
      dispatch({ type: 'set', empresaLogo: res.url })
      // [RGM] 2026-09-15 — PERSISTE el logo: el upload solo devuelve la URL de
      // R2; sin este guardado el logo se perdía al refrescar (quedaba solo en
      // pantalla hasta pulsar "Guardar Cambios").
      await guardarCuenta(res.url)
    }

    try {
      // Ruta 1: resize a webp (150px) via canvas
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('No se pudo decodificar la imagen'))
        image.src = URL.createObjectURL(file)
      })
      const canvas = document.createElement('canvas')
      const MAX_WIDTH = 150
      const scaleSize = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1
      canvas.width = Math.max(1, Math.round(img.width * scaleSize))
      canvas.height = Math.max(1, Math.round(img.height * scaleSize))
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const dataUrl = canvas.toDataURL('image/webp', 0.6)
        const blob = await (await fetch(dataUrl)).blob()
        await subir(blob, 'logo.webp')
        return
      }
      throw new Error('canvas no disponible')
    } catch (err: any) {
      // Ruta 2 (fallback): subir el archivo ORIGINAL sin transformar
      try {
        await subir(file, `logo${file.name.slice(file.name.lastIndexOf('.')) || '.png'}`)
      } catch (uploadErr: any) {
        setError('Error subiendo logo: ' + (uploadErr?.message ?? 'desconocido'))
      }
    }
  }

  const fecha = (v: string) => (v ? String(v).slice(0, 10) : '')

  // Pasarela/entidad del pago: los pagos de pasarela dejan "— PayPal / — ePayco"
  // en la observación; el resto sale del combo GX de entidad. [RGM] 2026-09-16.
  const pasarelaDe = (p: PagoRow) => {
    const obs = p.pagoObservacion ?? ''
    if (obs.includes('PayPal')) return 'PayPal'
    if (obs.includes('ePayco')) return 'ePayco'
    return ENTIDAD_PAGO[p.pagoEntidad] ?? String(p.pagoEntidad)
  }

  // Cuentas de cobro pendientes (0 = generada, 2 = comprobante subido): con
  // más de una se ofrece el pago total destacado (el backend cobra la suma). [RGM]
  const cuentasPendientes = (cuentasCobro?.cuentas ?? []).filter(
    (c) => c.estado === 0 || c.estado === 2)

  // Módulo Facturación GX (smallint): activo si > 0 (empresa.cs:1967 —
  // A209... = 0/1). Es SOLO ESTADO en Mi Cuenta: se activa al confirmarse el
  // pago del módulo (pasarela/PagoTipo 5), no gratis desde acá.
  const activaFacturacion = Number(cuenta?.moduloFacturacion ?? 0) > 0
  // [RGM] 2026-10-07 (feature F2 — "si selecciono solo facturación se
  // inhabilita módulo de facturación"): en SOLO FACTURACIÓN el Módulo
  // Facturación queda FIJO (activo, no editable — es la base del motor).
  const facturacionFija = cuenta?.modo === 'FACTURACION'

  // Precios del catálogo por key (null mientras carga o si el módulo no está
  // en el catálogo). [RGM]
  const precioDeModulo = (key: string): number | null => {
    const m = modulosCatalogo.find((x) => x.key === key)
    return m ? Number(m.precio ?? 0) : null
  }
  const precioFacturacion = precioDeModulo(KEY_FACTURACION)
  const precioFotos = precioDeModulo(KEY_FOTOS)
  const precioImpresion = precioDeModulo(KEY_IMPRESION)
  const precioWhatsapp = precioDeModulo(KEY_WHATSAPP)
  // Resumen por ruta (GET /rutas) de los módulos de impresión/WhatsApp. [RGM]
  // Estado por ruta (GET /rutas — misma fuente que RutasPage): cada switch de
  // la lista de rutas refleja el flag de SU ruta. [RGM]

  // Zona horaria informativa: se deriva del PaisNombre seleccionado, que en BD
  // tiene el formato "PAIS - Zona" (ej. "COLOMBIA - America/Bogota"). Si el
  // nombre no trae zona, se usa la del servidor.
  const zonaHoraria = (() => {
    const nombrePais = PAISES.find(([cod]) => cod === Number(cuenta?.paisCodigo))?.[1] ?? ''
    return nombrePais.includes(' - ')
      ? nombrePais.split(' - ').slice(1).join(' - ')
      : 'Sin zona configurada (usa la del servidor)'
  })()

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Mi Cuenta</h4>
        </CCol>
      </CRow>

      {/* Cabecera de informacion sin tarjeta (fondo transparente) */}
      <CRow className="mb-4 align-items-center">
        <CCol xs="auto">
          {logoPreview ? (
            <img
              src={logoPreview}
              alt="Logo empresa"
              style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--cui-border-color)' }}
            />
          ) : (
            <div style={{ width: 80, height: 80, backgroundColor: 'var(--cui-tertiary-bg)', borderRadius: 8 }} className="d-flex align-items-center justify-content-center text-body-secondary">
              <small>Sin logo</small>
            </div>
          )}
        </CCol>
        <CCol>
          <h3 className="mb-0 fw-bold">{cuenta?.nombre}</h3>
          <div className="text-body-secondary h5 mb-0">Código: <strong>{cuenta?.codigo}</strong></div>
          <CFormInput
            type="file"
            accept="image/*"
            onChange={onLogoChange}
            className="mt-2"
            style={{ maxWidth: 280 }}
          />
        </CCol>
      </CRow>

      {error && <CAlert color="danger" dismissible onClose={() => setError('')}>{error}</CAlert>}
      {exito && <CAlert color="success" dismissible onClose={() => setExito('')}>{exito}</CAlert>}

      <CCard className="mb-4">
        <CCardBody>
          {cargando ? (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : cuenta ? (
            <CForm onSubmit={guardar}>
              <CTabs activeItemKey={activeKey} onChange={(k) => setActiveKey(Number(k))}>
                <CTabList variant="tabs">
                  <CTab itemKey={1}>Perfil</CTab>
                  <CTab itemKey={2}>Empresa</CTab>
                  <CTab itemKey={3}>Plan</CTab>
                  {/* [RGM] Tienda de módulos y servicios: los módulos salieron
                      del tab Plan (Facturación movida acá). */}
                  <CTab itemKey={12}>Módulos y servicios</CTab>
                  <CTab itemKey={4}>Seguridad</CTab>
                  <CTab itemKey={5}>Notificaciones</CTab>
                  <CTab itemKey={6}>Miembros</CTab>
                  <CTab itemKey={7}>Permisos</CTab>
                  <CTab itemKey={8}>Pagos</CTab>
                  <CTab itemKey={9}>Rutas</CTab>
                  <CTab itemKey={10}>Metas</CTab>
                  {/* [RGM] 2026-09-28 (reporte Rafael): el tab TronIA sirve
                      para ELEGIR/CAMBIAR el plan del servicio → visible con la
                      empresa sin mora (con o sin plan; el panel permite
                      activar/cambiar). */}
                  {puedeGestionarTronia && <CTab itemKey={11}>TronIA</CTab>}
                </CTabList>

                <CTabContent className="py-3">
                  {/* ============ 1 PERFIL ============ */}
                  <CTabPanel itemKey={1}>
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Perfil de la Empresa</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Codigo</CFormLabel>
                            <CFormInput value={cuenta.codigo} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Nombre</CFormLabel>
                            {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                            <CFormInput value={cuenta.nombre} onChange={(e) => set('nombre', e.target.value.toUpperCase())} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Email</CFormLabel>
                            <CFormInput value={cuenta.email} onChange={(e) => set('email', e.target.value)} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>WhatsApp</CFormLabel>
                            <CFormInput value={cuenta.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Repre Legal</CFormLabel>
                            <CFormInput value={cuenta.repreLegal} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Fecha Creacion</CFormLabel>
                            <CFormInput value={fecha(cuenta.fechaCreacion)} readOnly />
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ 2 EMPRESA ============ */}
                  <CTabPanel itemKey={2}>
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Datos de la Empresa</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Direccion</CFormLabel>
                            <CFormInput value={cuenta.direccion} onChange={(e) => set('direccion', e.target.value)} onBlur={(e) => set('direccion', capitalizarPalabras(e.target.value))} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Telefono</CFormLabel>
                            <CFormInput value={cuenta.telefono} onChange={(e) => set('telefono', e.target.value)} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Pais</CFormLabel>
                            <CFormSelect
                              value={Number(cuenta.paisCodigo)}
                              onChange={(e) => set('paisCodigo', Number(e.target.value))}
                            >
                              {PAISES.map(([cod, nombre]) => (
                                <option key={cod} value={cod}>{nombre}</option>
                              ))}
                            </CFormSelect>
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Ciudad</CFormLabel>
                            <CFormInput value={cuenta.ciudad} onChange={(e) => set('ciudad', e.target.value)} onBlur={(e) => set('ciudad', capitalizarPalabras(e.target.value))} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Nombre Moneda</CFormLabel>
                            <CFormInput value={cuenta.nombreMoneda} onChange={(e) => set('nombreMoneda', e.target.value)} onBlur={(e) => set('nombreMoneda', capitalizarPalabras(e.target.value))} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Simbolo Moneda</CFormLabel>
                            <CFormInput value={cuenta.tipoMoneda} onChange={(e) => set('tipoMoneda', e.target.value)} />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Zona Horaria</CFormLabel>
                            <CFormInput value={zonaHoraria} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Tipo Servicio</CFormLabel>
                            <CFormInput value={cuenta.tipoServicio} onChange={(e) => set('tipoServicio', e.target.value)} onBlur={(e) => set('tipoServicio', capitalizarPalabras(e.target.value))} />
                          </CCol>
                          <CCol md={12}>
                            <CFormLabel>Descripcion</CFormLabel>
                            <CFormInput value={cuenta.descripcion} onChange={(e) => set('descripcion', e.target.value)} onBlur={(e) => set('descripcion', capitalizarPalabras(e.target.value))} />
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ 3 PLAN ============ */}
                  <CTabPanel itemKey={3}>
                    {/* [RGM] 2026-09-16 (reporte Rafael): el plan contratado se
                        veía en ningún lado — EmpresaLibreNumerico2 (1/2/3). */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Plan SiprinIA</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Plan Contratado</CFormLabel>
                            <CFormInput value={PLAN_NOMBRE[Number(cuenta.plan)] ?? 'Sin plan'} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Estado</CFormLabel>
                            {/* [RGM] 2026-09-16: estados GX reales (la réplica
                                usaba 1 = "Activa" — invertido). */}
                            <CFormInput value={estadoEmpresa(Number(cuenta.estado ?? 0)).label} readOnly />
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ 12 MÓDULOS Y SERVICIOS (tienda) ============ */}
                  <CTabPanel itemKey={12}>
                    <small className="text-body-secondary d-block mb-3">
                      Servicios adicionales de tu empresa — activá con el switch según el servicio.
                    </small>

                    {/* [RGM] 2026-10-07 (feature F2 — pedido Rafael: "Motor
                        del sistema" arriba en Módulos y servicios; "los tres
                        switch en las tres tarjetas"): radio — Solo Préstamos
                        (default) · Híbrido · Solo Facturación. Aplica INLINE. */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Motor del sistema</CCardHeader>
                      <CCardBody>
                        <div className="text-body-secondary" style={{ fontSize: 12 }}>
                          Define el motor de tu operación financiera: cartera,
                          facturación y recaudo de cartera móvil por app, con
                          asistencia de inteligencia artificial. Solo cambia los
                          módulos habilitados — tus tarifas y tus datos permanecen
                          intactos.
                        </div>
                        <CRow className="g-2 mt-1">
                          <CCol md={4}>
                            <CCard className={`h-100 ${motor === 'PRESTAMOS' ? 'border-primary border-2' : 'opacity-75'}`}>
                              <CCardBody className="py-2">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">Solo Préstamos</div>
                                  <CFormSwitch
                                    id="motor-prestamos-cuenta"
                                    label=""
                                    checked={motor === 'PRESTAMOS'}
                                    disabled={guardandoModo}
                                    onChange={() => cambiarMotor('PRESTAMOS')}
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Para empresas dedicadas al otorgamiento y cobro de
                                  préstamos: desembolsos, cuotas, abonos, mora y
                                  recaudo de cartera móvil por app.
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                          <CCol md={4}>
                            <CCard className={`h-100 ${motor === 'HIBRIDO' ? 'border-primary border-2' : 'opacity-75'}`}>
                              <CCardBody className="py-2">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">Híbrido</div>
                                  <CFormSwitch
                                    id="motor-hibrido-cuenta"
                                    label=""
                                    checked={motor === 'HIBRIDO'}
                                    disabled={guardandoModo}
                                    onChange={() => cambiarMotor('HIBRIDO')}
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Para empresas que prestan y además venden mercancías
                                  a crédito: cartera de préstamos y cuentas por
                                  cobrar de facturación, unificadas en una sola ruta
                                  de recaudo de cartera móvil por app.
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                          <CCol md={4}>
                            <CCard className={`h-100 ${motor === 'FACTURACION' ? 'border-primary border-2' : ''}`}>
                              <CCardBody className="py-2">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">Solo Facturación</div>
                                  <CFormSwitch
                                    id="motor-facturacion-cuenta"
                                    label=""
                                    checked={motor === 'FACTURACION'}
                                    disabled={guardandoModo}
                                    onChange={() => cambiarMotor('FACTURACION')}
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Para comercios que venden de contado o a crédito y
                                  recaudan por rutas, sin otorgar préstamos: la venta
                                  a crédito genera su plan de cuotas y se recauda con
                                  cartera móvil por app.
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>

                    {/* [RGM] Grupos EN TARJETA, módulos en cuadros 2 por fila con
                        switch (organización Rafael 2026-09-28). */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Cobro mensual</CCardHeader>
                      <CCardBody>
                        <CRow className="g-2">
                          <CCol md={6}>
                            <CCard className="h-100">
                              <CCardBody className="py-2">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">Fotos de clientes</div>
                                  <CFormSwitch
                                    id="mod-fotos"
                                    label="Activo"
                                    checked={cuenta.fotosAdicionalMes}
                                    disabled={guardandoFotos || cuenta.fotosAdicionalMes}
                                    onChange={(e) => activarFotos(e.target.checked)}
                                    title={cuenta.fotosAdicionalMes ? 'Activado — para desactivar contactá al administrador' : 'Activar Fotos de clientes'}
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Guardá fotos de tus clientes (adicionales del mes).
                                  {precioFotos != null ? ` ${fmtMoneda(precioFotos)}/mes por ruta.` : ''}{' '}
                                  Se cobra en tu mensualidad.
                                  {cuenta.fotosAdicionalMes
                                    ? ' Para desactivar, contactá al administrador.'
                                    : ''}
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                          <CCol md={6}>
                            <CCard className="h-100">
                              <CCardBody className="py-2">
                                {/* [RGM] 2026-09-29 (reporte Rafael): el switch y el
                                    texto muestran el ESTADO REAL de TronIA —
                                    PRUEBA ya no se ve como "Plan activo". */}
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">TronIA</div>
                                  <CFormSwitch
                                    id="mod-tronia"
                                    label={troniaEstado === null ? '…'
                                      : troniaEstado === 'PRUEBA' ? 'Prueba'
                                        : (troniaEstado === 'ACTIVO' || troniaEstado === 'SUPERADMIN') ? 'Activo'
                                          : 'Inactivo'}
                                    checked={troniaEstado === 'ACTIVO' || troniaEstado === 'SUPERADMIN'}
                                    disabled={troniaEstado === null || !puedeGestionarTronia}
                                    onChange={() => setActiveKey(11)}
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Asistente con inteligencia artificial para tu cobranza.{' '}
                                  {troniaEstado === 'PRUEBA' ? 'Prueba gratuita activa.'
                                    : (troniaEstado === 'ACTIVO' || troniaEstado === 'SUPERADMIN') ? 'Plan activo.'
                                      : 'Elegí tu plan para activarlo.'}
                                </div>
                              </CCardBody>
                            </CCard>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>

                    {/* [RGM] PAGO ÚNICO (organización Rafael 2026-09-28):
                        dentro van la tarjeta "Módulos de recibo para el cliente"
                        (tickets juntos) y de ÚLTIMO el módulo Facturación. La
                        factura a crédito crea el préstamo a la factura (sale en
                        la ruta de préstamos). Los recibos se activan POR RUTA. */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Pago único</CCardHeader>
                      <CCardBody>
                        <CCard className="mb-2">
                          <CCardHeader className="fw-semibold">Módulos de recibo para el cliente</CCardHeader>
                          <CCardBody>
                            <CRow className="g-2">
                              <CCol md={6}>
                                <CCard className="h-100">
                                    <CCardBody className="py-2">
                                      <div className="fw-semibold">Tickets de impresión</div>
                                      <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                        Impresión de recibo (ticket) por Bluetooth.
                                        {precioImpresion != null ? ` ${fmtMoneda(precioImpresion)} por ruta.` : ''}
                                      </div>
                                    {/* [RGM] 2026-09-28 (pedido Rafael): lista
                                        vertical de rutas con su switch al frente
                                        (estado por ruta; se activan en Rutas). */}
                                    {rutasModulos.map((r) => (
                                      <div key={`imp-${r.zonaCodigo ?? r.zonaNombre}`}
                                        className="d-flex align-items-center justify-content-between gap-2 mt-1">
                                        <span style={{ fontSize: 13 }}>
                                          {r.zonaNombre?.trim() || `Ruta ${r.zonaCodigo ?? ''}`}
                                        </span>
                                        {Number(r.zonaImpresora ?? 0) === 1 ? (
                                          <CFormSwitch
                                            id={`impresion-ruta-${r.zonaCodigo ?? r.zonaNombre}`}
                                            label=""
                                            checked
                                            disabled
                                            title="Activado — para desactivar contactá al administrador"
                                          />
                                        ) : r.pendienteImpresora === true ? (
                                          // [RGM] 2026-09-29 (pedido Rafael): pago
                                          // SUBIDO pendiente de confirmación.
                                          <span className="text-warning small fw-semibold">
                                            Pendiente de confirmación
                                          </span>
                                        ) : (
                                          <CFormSwitch
                                            id={`impresion-ruta-${r.zonaCodigo ?? r.zonaNombre}`}
                                            label=""
                                            checked={false}
                                            title={`Activar Solo Ticket — ${r.zonaNombre?.trim() || `Ruta ${r.zonaCodigo ?? ''}`}`}
                                            onChange={(e) => {
                                              if (e.target.checked)
                                                abrirPagoModulo('activar-impresora', r, 'Tickets de impresión')
                                            }}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </CCardBody>
                                </CCard>
                              </CCol>
                              <CCol md={6}>
                                <CCard className="h-100">
                                    <CCardBody className="py-2">
                                      <div className="fw-semibold">Whatsapp + Ticket</div>
                                      <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                        Confirmación de pago por WhatsApp al cliente.
                                        {precioWhatsapp != null ? ` ${fmtMoneda(precioWhatsapp)} por ruta.` : ''}
                                      </div>
                                    {/* [RGM] 2026-09-28 (pedido Rafael): lista
                                        vertical de rutas con su switch al frente. */}
                                    {rutasModulos.map((r) => (
                                      <div key={`ws-${r.zonaCodigo ?? r.zonaNombre}`}
                                        className="d-flex align-items-center justify-content-between gap-2 mt-1">
                                        <span style={{ fontSize: 13 }}>
                                          {r.zonaNombre?.trim() || `Ruta ${r.zonaCodigo ?? ''}`}
                                        </span>
                                        {Number(r.zonaSMS ?? 0) === 1 ? (
                                          <CFormSwitch
                                            id={`whatsapp-ruta-${r.zonaCodigo ?? r.zonaNombre}`}
                                            label=""
                                            checked
                                            disabled
                                            title="Activado — para desactivar contactá al administrador"
                                          />
                                        ) : r.pendienteWhatsapp === true ? (
                                          // [RGM] 2026-09-29 (pedido Rafael): pago
                                          // SUBIDO pendiente de confirmación.
                                          <span className="text-warning small fw-semibold">
                                            Pendiente de confirmación
                                          </span>
                                        ) : (
                                          <CFormSwitch
                                            id={`whatsapp-ruta-${r.zonaCodigo ?? r.zonaNombre}`}
                                            label=""
                                            checked={false}
                                            title={`Activar WS + Ticket — ${r.zonaNombre?.trim() || `Ruta ${r.zonaCodigo ?? ''}`}`}
                                            onChange={(e) => {
                                              if (e.target.checked)
                                                abrirPagoModulo('activar-whatsapp', r, 'Ticket por WhatsApp')
                                            }}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </CCardBody>
                                </CCard>
                              </CCol>
                            </CRow>
                            <div className="mt-3">
                              <CButton size="sm" color="primary" variant="outline" type="button"
                                onClick={() => navigate('/rutas')}>
                                Activar por ruta en Rutas
                              </CButton>
                            </div>
                          </CCardBody>
                        </CCard>

                        <CRow className="g-2">
                          <CCol md={6}>
                            <CCard className="h-100">
                              <CCardBody className="py-2">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                  <div className="fw-semibold">Módulo Facturación</div>
                                  <CFormSwitch
                                    id="mod-facturacion-estado"
                                    label="Activo"
                                    checked={activaFacturacion || facturacionFija}
                                    disabled
                                  />
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Facturá mercancías de contado o a crédito; el crédito se crea
                                  como préstamo a la factura, para que salga en la ruta de préstamos.
                                  {precioFacturacion != null ? ` ${fmtMoneda(precioFacturacion)} — pago único.` : ''}{' '}
                                  {facturacionFija
                                    ? 'FIJO por tu motor Solo Facturación (no editable).'
                                    : 'Se activa al confirmar el pago.'}
                                </div>
                                {/* [RGM] 2026-10-05 (pedido Rafael): % de IVA de la
                                    empresa — lo setea ACÁ si usa IVA (default 0).
                                    EmpresaLibreNumerico1; la factura lo aplica al
                                    final: IVA = (SubTotal − Descuento) × % / 100.
                                    ACTUALIZACIÓN INLINE: se guarda al salir del
                                    campo (esta tarjeta no tiene Guardar Cambios). */}
                                {(activaFacturacion || facturacionFija) && (
                                  <div className="d-flex align-items-center gap-2 mt-2" style={{ maxWidth: 300 }}>
                                    <CFormLabel className="mb-0" htmlFor="emp-iva">% IVA</CFormLabel>
                                    <CFormInput id="emp-iva" type="number" min={0} max={100} step={1}
                                      value={cuenta.iva ? String(cuenta.iva) : ''}
                                      placeholder="0"
                                      onChange={(e) => set('iva', e.target.value)}
                                      onBlur={actualizarIvaInline}
                                      disabled={guardandoIva}
                                      title="Porcentaje de IVA que se calcula al final de tus facturas (0 = sin IVA) — se guarda al salir del campo" />
                                    {guardandoIva && <CSpinner size="sm" />}
                                  </div>
                                )}
                                {!activaFacturacion && !facturacionFija && (
                                  <div className="d-flex flex-wrap gap-2 mt-2">
                                    <CButton size="sm" color="primary" type="button"
                                      disabled={pagando}
                                      onClick={() => pagarMensualidad('paypal', 'activar-facturacion')}>
                                      Pagar con PayPal
                                    </CButton>
                                    <CButton size="sm" color="primary" variant="outline" type="button"
                                      disabled={pagando}
                                      onClick={() => pagarMensualidad('epayco', 'activar-facturacion')}>
                                      Pagar con ePayco
                                    </CButton>
                                    <CButton size="sm" color="secondary" variant="outline" type="button"
                                      onClick={() => {
                                        setTipoInicialSubirPago(TIPO_PAGO_FACTURACION)
                                        setSubirPagoAbierto(true)
                                      }}>
                                      Subir pago
                                    </CButton>
                                  </div>
                                )}
                              </CCardBody>
                            </CCard>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ TABS SIN DATOS REALES (estructura vacia) ============ */}
                  <CTabPanel itemKey={4}>
                    <div className="p-4 text-center text-body-secondary">Sin informacion disponible</div>
                  </CTabPanel>
                  {/* itemKey 5 = Notificaciones (panel REAL más abajo) — el
                      placeholder duplicado se retiró (review GGA 2026-10-05:
                      dos CTabPanel con el mismo itemKey renderizaban apilados). */}
                  <CTabPanel itemKey={6}>
                    <div className="p-4 text-center text-body-secondary">Sin informacion disponible</div>
                  </CTabPanel>
                  <CTabPanel itemKey={7}>
                    <div className="p-4 text-center text-body-secondary">Sin informacion disponible</div>
                  </CTabPanel>
                  {/* [RGM] Tab TronIA — estado + elegir/cambiar plan (Fase 6) */}
                  {puedeGestionarTronia && (
                    <CTabPanel itemKey={11}>
                      <TroniaPanel />
                    </CTabPanel>
                  )}

                  {/* ============ 8 PAGOS (todos reales) ============ */}
                  <CTabPanel itemKey={8}>
                    {/* Acceso a la pantalla de pago de la suscripción (/pago):
                        reconecta PagoPage (reorganizada) desde Configuración →
                        Mi Cuenta → Pagos. PRIMER lugar de la pestaña. [RGM] */}
                    {/* [RGM] 2026-09-29 (B5 aprobado Rafael): tarjeta por plan —
                        0 (sin plan): elegir plan + subir pago; Uni(1)/Duo(2):
                        SOLO actualizar (pasarela + comprobante de la DIFERENCIA);
                        Empresa(3): la tarjeta no se muestra. */}
                    {cuenta && Number(cuenta.plan ?? 0) !== 3 && (
                      <CCard className="mb-3">
                        <CCardHeader className="fw-semibold">Suscripción SiprinIA</CCardHeader>
                        <CCardBody className="d-flex flex-column gap-2">
                          {Number(cuenta.plan ?? 0) === 0 ? (
                            <>
                              <div>
                                <CButton color="primary" type="button" onClick={() => navigate('/pago')}>
                                  Elegir plan y pagar
                                </CButton>
                              </div>
                              {/* [RGM] Subir pago por OTRO medio (transferencia/giro):
                                  abre el modal COMPARTIDO (components/SubirPagoModal)
                                  — el pago queda pendiente de confirmación del
                                  superadmin. */}
                              <div>
                                <CButton color="primary" variant="outline" type="button"
                                  onClick={() => {
                                    setTipoInicialSubirPago('')
                                    setSubirPagoAbierto(true)
                                  }}>
                                  Subir pago de Suscripción
                                </CButton>
                                <div className="text-body-secondary small mt-1">
                                  Si pagaste por otro medio (datos de pago)
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <CButton color="primary" type="button" onClick={() => navigate('/pago')}>
                                  Actualizar a un plan mayor
                                </CButton>
                              </div>
                              <div>
                                <CButton color="primary" variant="outline" type="button"
                                  onClick={() => {
                                    setTipoInicialSubirPago(TIPO_PAGO_ACTUALIZACION)
                                    setSubirPagoAbierto(true)
                                  }}>
                                  Subir Pago de Actualizar a Plan Mayor
                                </CButton>
                                <div className="text-body-secondary small mt-1">
                                  Subí el comprobante de la DIFERENCIA del plan nuevo
                                </div>
                              </div>
                            </>
                          )}
                        </CCardBody>
                      </CCard>
                    )}

                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Pagos</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Tarifa Mes</CFormLabel>
                            <CFormInput value={cuenta.tarifaMes} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Ultimo Pago</CFormLabel>
                            <CFormInput value={fecha(cuenta.ultimoPago)} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Proximo Cobro</CFormLabel>
                            <CFormInput value={fecha(cuenta.proximoCobro)} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Otros saldos pendientes</CFormLabel>
                            <CFormInput value={cuenta.saldoActual} readOnly />
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Motivo Saldo</CFormLabel>
                            <CFormInput value={cuenta.motivoSaldo} readOnly />
                          </CCol>
                        </CRow>
                        {/* [RGM] 2026-09-29 (pedido Rafael): (a) empresa en
                            PRUEBA (6) NO muestra prorrateo/meses — no tiene
                            cobro ("En prueba (sin cobro)"); (b) sin guiones
                            entre números (parecían restas); (c) sin la palabra
                            "Base" — la tarifa se describe con sus sumandos. */}
                        {resumenPago && (
                          <div className="mt-3 pt-2 border-top">
                            {Number(cuenta?.estado ?? 0) === 6 ? (
                              <div className="fw-semibold">En prueba (sin cobro)</div>
                            ) : (
                              <>
                                <div className="fw-semibold">
                                  Saldo Total:{' '}
                                  {fmtMoneda((cuentasCobro?.totalPendiente ?? 0) + Number(resumenPago.otrosSaldosPendientes ?? 0))}
                                </div>
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  {(cuentasCobro?.cuentas?.length ?? 0) > 0
                                    ? <>Cuentas de cobro pendientes: {cuentasCobro?.cuentas?.length} ({fmtMoneda(cuentasCobro?.totalPendiente ?? 0)})</>
                                    : <>Meses vencidos: {resumenPago.mesesVencidos ?? 0} × {fmtMoneda(resumenPago.tarifaMensual ?? 0)}</>}
                                </div>
                                {Number(resumenPago.otrosSaldosPendientes ?? 0) !== 0 && (
                                  <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                    Otros saldos pendientes: {fmtMoneda(resumenPago.otrosSaldosPendientes)}
                                    {String(resumenPago.motivoSaldo ?? '').trim()
                                      ? ` (${String(resumenPago.motivoSaldo).trim()})` : ''}
                                  </div>
                                )}
                                <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                  Tarifa Mensual: {fmtMoneda(resumenPago.tarifaMensual ?? 0)}
                                </div>
                                {(Number(resumenPago.tarifaFotos ?? 0) !== 0
                                  || Number(resumenPago.tarifaTronia ?? 0) !== 0
                                  || Number(resumenPago.tarifaAjuste ?? 0) !== 0) && (
                                  <div className="text-body-secondary" style={{ fontSize: 12 }}>
                                    ({fmtMoneda(resumenPago.tarifaBase ?? 0)} de mantenimiento
                                    {Number(resumenPago.tarifaFotos ?? 0) !== 0 && (
                                      <> + {fmtMoneda(resumenPago.tarifaFotos)} de fotos ({resumenPago.tarifaRutas ?? 0} rutas)</>
                                    )}
                                    {Number(resumenPago.tarifaTronia ?? 0) !== 0 && (
                                      <> + {fmtMoneda(resumenPago.tarifaTronia)} de TronIA</>
                                    )}
                                    {Number(resumenPago.tarifaAjuste ?? 0) !== 0 && (
                                      <> + {fmtMoneda(resumenPago.tarifaAjuste)} de ajuste</>
                                    )})
                                  </div>
                                )}
                                {/* [RGM] 2026-10-23 (pedido Rafael): el pago
                                    con ePayco se traslada al cliente con el 5%
                                    de comisión de la pasarela. El total con
                                    recargo lo calcula el API
                                    (ReglasPago.MontoEpaycoConRecargo →
                                    EpaycoTotal), no el web. */}
                                {Number(resumenPago.epaycoTotal ?? 0) > 0 && (
                                  <div className="mt-2">
                                    <div className="fw-semibold">
                                      Total con ePayco: {fmtMoneda(resumenPago.epaycoTotal)}
                                    </div>
                                    <small className="text-body-secondary">
                                      El pago con ePayco incluye un 5% de comisión de la pasarela.
                                    </small>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </CCardBody>
                    </CCard>

                    {/* CUENTAS DE COBRO MENSUALES [RGM]: el job genera la cuenta
                        el día 5; cada cuenta se paga con la pasarela (el backend
                        cobra la SUMA de las pendientes y las confirma) o se sube
                        el comprobante (estado 2, espera la confirmación del admin). */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Cuentas de cobro</CCardHeader>
                      <CCardBody>
                        {/* Saldo Vencido en ROJO solo si > 0. */}
                        {cuentasCobro && cuentasCobro.saldoVencido > 0 && (
                          <div className="fw-bold text-danger mb-2">
                            Saldo Vencido: {fmtMoneda(cuentasCobro.saldoVencido)}
                          </div>
                        )}
                        {/* Más de una pendiente (0/2): pago total destacado — el
                            backend cobra la SUMA de las cuentas. */}
                        {cuentasPendientes.length > 1 && (
                          <div className="mb-3">
                            <CButton color="primary" disabled={pagando}
                              onClick={() => pagarMensualidad('paypal')}>
                              Pagar total — {fmtMoneda(cuentasCobro?.totalPendiente)}
                            </CButton>
                          </div>
                        )}
                        <div className="table-responsive">
                          <table className="table table-sm align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Período</th>
                                <th>Valor</th>
                                <th>Estado</th>
                                <th>Comprobante</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {!cuentasCobro || cuentasCobro.cuentas.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-center text-body-secondary">
                                    Sin cuentas de cobro.
                                  </td>
                                </tr>
                              ) : cuentasCobro.cuentas.map((c) => {
                                const vencimiento = estadoVencimiento(c.fecha)
                                return (
                                <tr key={c.pagoNro}>
                                  <td>{c.periodo || '—'}</td>
                                  <td>{fmtMoneda(c.valor)}</td>
                                  <td>
                                    {estadoCuenta(c.estado)}
                                    {/* [RGM] 2026-10-02: vencimiento de la
                                        cuenta pendiente (POR VENCER 1-5 /
                                        VENCIDA desde el 6). */}
                                    {(c.estado === 0 || c.estado === 2) && (
                                      <>
                                        {' '}
                                        {vencimiento === 'VENCIDA'
                                          ? <CBadge color="danger">VENCIDA</CBadge>
                                          : <CBadge color="warning" className="text-dark">POR VENCER</CBadge>}
                                      </>
                                    )}
                                  </td>
                                  <td>
                                    {c.comprobanteUrl
                                      ? <a href={c.comprobanteUrl} target="_blank" rel="noreferrer">Ver</a>
                                      : '—'}
                                  </td>
                                  <td className="text-end">
                                    {c.estado === 0 && (
                                      <div className="d-flex gap-2 justify-content-end flex-wrap">
                                        <CButton size="sm" color="primary" disabled={pagando}
                                          onClick={() => pagarMensualidad('paypal')}>
                                          Pagar con PayPal
                                        </CButton>
                                        <CButton size="sm" color="info" className="text-white" disabled={pagando}
                                          onClick={() => pagarMensualidad('epayco')}>
                                          Pagar con ePayco
                                        </CButton>
                                        {/* Input file oculto por cuenta: el botón
                                            "Subir comprobante" lo abre. */}
                                        <input type="file" className="d-none"
                                          id={`comprobante-cuenta-${c.pagoNro}`}
                                          accept="image/*,.jpg,.jpeg,.png,.webp"
                                          onChange={(e) => {
                                            const f = e.target.files?.[0]
                                            if (f) subirComprobanteCuenta(c.pagoNro, f)
                                            e.target.value = ''
                                          }} />
                                        <CButton size="sm" color="secondary" variant="outline"
                                          disabled={subiendoComprobanteCuenta === c.pagoNro}
                                          onClick={() => document.getElementById(`comprobante-cuenta-${c.pagoNro}`)?.click()}>
                                          {subiendoComprobanteCuenta === c.pagoNro ? 'Subiendo...' : 'Subir comprobante'}
                                        </CButton>
                                      </div>
                                    )}
                                    {c.estado === 2 && (
                                      <span className="text-body-secondary small">
                                        Esperando confirmación del admin.
                                      </span>
                                    )}
                                  </td>
                                </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="text-body-secondary mt-3" style={{ fontSize: 12 }}>
                          El mantenimiento se liquida VENCIDO el 5 de cada mes (la
                          primera vez prorrateado). También puedes pagar por
                          transferencia Bancolombia o giro Western Unión.
                          <br />
                          Los pagos con PayPal tienen un recargo del 7% (comisión
                          de la pasarela).
                        </div>
                      </CCardBody>
                    </CCard>

                    {/* [RGM] 2026-10-23 (pedido Rafael): "Datos de pago"
                        COMPARTIDOS con PagoPage (components/DatosDePago) —
                        Bancolombia, QR Bre-B y giros internacionales junto a
                        los medios de pago de la mensualidad. El botón abre el
                        MISMO modal de comprobante de esta pantalla. El pedido
                        posterior: tarjeta a LO ANCHO con el QR a la derecha
                        (layout="split"). */}
                    <div className="mb-3">
                      <DatosDePago layout="split" />
                    </div>

                    {/* HISTORIAL DE PAGOS [RGM] 2026-09-16 (pedido Rafael): los
                        pagos aceptados por las pasarelas y los subidos a mano,
                        con toda la info + la pasarela. */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Historial de pagos</CCardHeader>
                      <CCardBody className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              {/* [RGM] 2026-11-04 (pedido Rafael): el cliente NO
                                  ve el número de recibo (#) — no debe saber
                                  cuántos pagos lleva. El superadmin conserva el
                                  Nro en su grilla de Pagos. */}
                              <th>Fecha</th>
                              {/* [RGM] 2026-10-10 (pedido Rafael: "el pago
                                  tiene dos fechas: la de creación y la fecha
                                  en que se hizo el pago — la del
                                  comprobante"): segunda fecha visible en el
                                  historial (PagoFechaPago, la que el admin
                                  edita en PagosPage/confirma). */}
                              <th>Fecha Pago</th>
                              <th>Valor</th>
                              <th>Tipo</th>
                              <th>Pasarela</th>
                              <th>Referencia</th>
                              <th>Estado</th>
                              <th>Observación</th>
                              <th>Comprobante</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagosVisibles.length === 0 && (
                              <tr>
                                <td colSpan={10} className="text-center text-body-secondary">
                                  Sin pagos registrados.
                                </td>
                              </tr>
                            )}
                            {pagosVisibles.map((p) => (
                              <tr key={p.pagoNro}>
                                <td>{fecha(p.pagoFecha)}</td>
                                <td>{p.pagoFechaPago ? fecha(p.pagoFechaPago) : '—'}</td>
                                <td>{fmtMoneda(p.pagoValor)}</td>
                                <td>{TIPO_PAGO[p.pagoTipo] ?? p.pagoTipo}</td>
                                <td>{pasarelaDe(p)}</td>
                                <td>{p.pagoRefrencia || '—'}</td>
                                <td>
                                  {p.pagoConfirmado === 1
                                    ? <span className="badge bg-success">Confirmado</span>
                                    : <span className="badge bg-warning text-dark">Pendiente</span>}
                                  {/* [RGM] 2026-10-09 (pedido Rafael — caso
                                      MAIN #19/#20): fila PENDIENTE que
                                      comparte referencia con OTRA pendiente
                                      de la empresa → posible duplicado
                                      (ANTES de confirmar). */}
                                  {Number(p.pagoConfirmado) === 2
                                    && duplicadosPendientes.has(claveDuplicadoPago(p)) && (
                                      <span className="badge text-warning border border-warning ms-1">
                                        Posible duplicado
                                      </span>
                                    )}
                                </td>
                                <td className="small text-body-secondary" style={{ maxWidth: 260, whiteSpace: 'normal' }}>
                                  {p.pagoObservacion}
                                </td>
                                <td>
                                  {p.comprobanteUrl
                                    ? <a href={p.comprobanteUrl} target="_blank" rel="noreferrer">Ver</a>
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ 9 RUTAS ============ */}
                  <CTabPanel itemKey={9}>
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Rutas</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Enrutar Manual?</CFormLabel>
                            <CFormSelect value={cuenta.enrutarManual} onChange={(e) => set('enrutarManual', Number(e.target.value))}>
                              <option value={0}>No</option>
                              <option value={1}>Sí</option>
                            </CFormSelect>
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Rutas</CFormLabel>
                            <CFormInput value={cuenta.rutas} readOnly />
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>

                    {/* [RGM] 2026-10-02 (decisión Rafael: "en Rutas queda bien"):
                        Días Laborables movido aquí desde el tab Plan. El sábado
                        se eliminó (confunde); solo Domingo y Festivo — afectan la
                        fórmula de fecha término del préstamo (ReglasFechas). */}
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Dias Laborables</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormCheck
                              id="dia-domingo"
                              label="Trabaja Domingo"
                              checked={cuenta.trabajaDomingo}
                              onChange={(e) => set('trabajaDomingo', e.target.checked)}
                            />
                          </CCol>
                          <CCol md={6}>
                            <CFormCheck
                              id="dia-festivo"
                              label="Trabaja Festivo"
                              checked={cuenta.trabajaFestivo}
                              onChange={(e) => set('trabajaFestivo', e.target.checked)}
                            />
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>

                  {/* ============ 10 METAS ============ */}
                  <CTabPanel itemKey={10}>
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Metas</CCardHeader>
                      <CCardBody>
                        <CRow className="g-3">
                          <CCol md={6}>
                            <CFormLabel>Porcentaje Mínimo al que presta en un Mes</CFormLabel>
                            <CFormInput type="number" value={cuenta.metaMinima} onChange={(e) => set('metaMinima', e.target.value)} onBlur={(e) => set('metaMinima', normalizarNumeroTexto(e.target.value))} />
                            <div className="text-body-secondary" style={{ fontSize: 12 }}>Ej: 10%</div>
                          </CCol>
                          <CCol md={6}>
                            <CFormLabel>Porcentaje Máximo al que presta en un mes</CFormLabel>
                            <CFormInput type="number" value={cuenta.metaMaxima} onChange={(e) => set('metaMaxima', e.target.value)} onBlur={(e) => set('metaMaxima', normalizarNumeroTexto(e.target.value))} />
                            <div className="text-body-secondary" style={{ fontSize: 12 }}>Ej: 30%</div>
                          </CCol>
                        </CRow>
                      </CCardBody>
                    </CCard>
                  </CTabPanel>
                  {/* [RGM] 2026-10-01 (pedido Rafael): el tab Notificaciones
                      ahora muestra los avisos (antes era placeholder) + la
                      nueva versión del APK con botón Descargar. */}
                  <CTabPanel itemKey={5}>
                    <CCard className="mb-3">
                      <CCardHeader className="fw-semibold">Notificaciones</CCardHeader>
                      <CCardBody>
                        <NotificacionesLista
                          avisos={notifAvisos}
                          descartar={descartarNotif}
                          onIr={(a) =>
                            a.descargar ? (window.location.href = a.descargar) : navigate(a.url ?? '/')
                          }
                        />
                      </CCardBody>
                    </CCard>
                  </CTabPanel>
                </CTabContent>
              </CTabs>
              {/* [RGM] 2026-09-15 (decisión Rafael): "Guardar Cambios" solo en los
                  tabs cuyos campos persiste guardar() — Perfil (1), Empresa (2),
                  Plan (3), Rutas (9) y Metas (10). En Módulos y servicios (12)
                  los switches guardan solos; Seguridad, Notificaciones, Miembros,
                  Permisos, Pagos y TronIA no tienen campos que guarde. */}
              {[1, 2, 3, 9, 10].includes(activeKey) && (
                <div className="d-flex justify-content-end mt-4 pt-3">
                  <CButton color="primary" type="submit">Guardar Cambios</CButton>
                </div>
              )}
            </CForm>
          ) : null}
        </CCardBody>
      </CCard>

      {/* [RGM] Modal COMPARTIDO "Subir pago de Suscripción" (el formulario GX
          del panel subirpagosistema, con comprobante obligatorio): el pago
          queda pendiente de confirmación del superadmin; al subir se refrescan
          la cuenta, el historial y las cuentas de cobro. */}
      <SubirPagoModal
        visible={subirPagoAbierto}
        onClose={() => setSubirPagoAbierto(false)}
        proximoCobro={cuenta?.proximoCobro}
        tipoInicial={tipoInicialSubirPago}
        planActual={Number(cuenta?.plan ?? 0)}
        onSubido={() => {
          cargar()
          cargarPagos()
          cargarCuentasCobro()
        }}
      />

      {/* [RGM] 2026-09-28 (pedido Rafael): pago del módulo de recibo por RUTA
          desde los switches del tab "Módulos y servicios" (PayPal / ePayco /
          comprobante). Al cerrar se recargan las rutas para refrescar los
          switches. */}
      {moduloPago && (
        <ModalPagoModulo
          visible
          tipo={moduloPago.tipo}
          zonaCodigo={moduloPago.zonaCodigo}
          etiqueta={moduloPago.etiqueta}
          onClose={() => { setModuloPago(null); cargarRutasModulos() }}
          onPagado={cargarRutasModulos}
        />
      )}
    </>
  )
}

export default CuentaPage
